import { z } from "zod";

/**
 * Why a location source of a Transaction has no location (`sources[].unavailable_cause` of the
 * Explorer API; `UnavailableCause` of `core`). `geoip_unavailable` is also a verification Reason
 * (the IP dataset could not be read); the IP assessment port's own `dataset_unavailable` is mapped
 * to it by `core` and never reaches the Explorer.
 *
 * The Explorer API and the integration events keep the wire type lenient (`enum | string`): a newer
 * `core` may emit a cause an older console does not know, and the console then shows the raw code.
 */
export const LOCATION_SOURCE_UNAVAILABLE_CAUSES = [
  "geoip_unavailable",
  "ip_not_allocated",
  "permission_denied",
  "position_unavailable",
  "not_collected",
  "not_available_in_browser",
] as const;
export const LocationSourceUnavailableCause = z
  .enum(LOCATION_SOURCE_UNAVAILABLE_CAUSES)
  .meta({ id: "LocationSourceUnavailableCause" });
export type LocationSourceUnavailableCause = z.infer<typeof LocationSourceUnavailableCause>;

export function isLocationSourceUnavailableCause(
  value: string,
): value is LocationSourceUnavailableCause {
  return (LOCATION_SOURCE_UNAVAILABLE_CAUSES as readonly string[]).includes(value);
}

/**
 * Causes of a Check that are not verification Reasons (`checks[].cause`): the informational cause
 * of a not-evaluated `travel_speed` Check and the specific causes of an `integrity` failure.
 * A `not_evaluated` Check may also carry a Reason code as its cause (e.g. `geoip_unavailable`,
 * `location_check_not_enabled`); those are resolved through the Reason catalog.
 */
export const CHECK_CAUSES = [
  "no_known_location",
  "sdk_version_invalid",
  "sdk_version_unsupported",
] as const;
export const CheckCause = z.enum(CHECK_CAUSES).meta({ id: "CheckCause" });
export type CheckCause = z.infer<typeof CheckCause>;

export function isCheckCause(value: string): value is CheckCause {
  return (CHECK_CAUSES as readonly string[]).includes(value);
}
